import React, { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Divider,
    Button,
    Typography,
    Box,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import Slider from '@mui/material/Slider';
import { Add, Save, Cancel, ExpandMore, Edit } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { motion } from 'framer-motion';
import Layout from './Layout';
import { colors } from '../colors';
import axiosInstance from '../axiosConfig';
import { Link } from '@mui/icons-material'; // Import icons
import { MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Notification from './Notification';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';

const TemplateCreate = () => {
    const [templateTitle, setTemplateTitle] = useState(''); // Template name
    const [sections, setSections] = useState([]); // Sections array
    const [selectedOption, setSelectedOption] = useState('Add');
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(null); // Tracks selected section
    const [isQuestionDialogOpen, setIsQuestionDialogOpen] = useState(false);
    const [newQuestion, setNewQuestion] = useState({ questionText: '', type: '' });
    const [conditionDialogOpen, setConditionDialogOpen] = useState(false);
    const navigate = useNavigate();
    const [notification, setNotification] = useState(null);
    // State for tracking the section to be deleted
    const [sectionToDelete, setSectionToDelete] = useState(null);
    const [deleteSectionDialogOpen, setDeleteSectionDialogOpen] = useState(false);

    const [isEditQuestionDialogOpen, setIsEditQuestionDialogOpen] = useState(false);
    const [editedQuestion, setEditedQuestion] = useState(null);
    const [editedSectionIndex, setEditedSectionIndex] = useState(null);
    const [editedQuestionIndex, setEditedQuestionIndex] = useState(null);

    const [isDeleteQuestionDialogOpen, setIsDeleteQuestionDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ sectionIndex: null, questionIndex: null });

    const handleOpenDeleteQuestionDialog = (sectionIndex, questionIndex) => {
        setDeleteTarget({ sectionIndex, questionIndex });
        setIsDeleteQuestionDialogOpen(true);
    };

    const handleCloseDeleteQuestionDialog = () => {
        setIsDeleteQuestionDialogOpen(false);
        setDeleteTarget({ sectionIndex: null, questionIndex: null });
    };

    const handleDeleteQuestion = () => {
        const { sectionIndex, questionIndex } = deleteTarget;
        if (sectionIndex !== null && questionIndex !== null) {
            const updatedSections = [...sections];
    
            // Remove the question
            updatedSections[sectionIndex].questions.splice(questionIndex, 1);
    
            // Update questionIds of remaining questions in the section
            updatedSections[sectionIndex].questions = updatedSections[sectionIndex].questions.map((question, index) => {
                let updatedQuestion = {
                    ...question,
                    questionId: index + 1,  // Reassign questionId sequentially starting from 1
                };
    
                // If the question is of type TABLE, update subQuestionIds as well
                if (updatedQuestion.type === 'TABLE') {
                    updatedQuestion.subQuestions = updatedQuestion.subQuestions.map((subQuestion, subIndex) => ({
                        ...subQuestion,
                        subQuestionId: `${updatedQuestion.questionId}.${subIndex + 1}`,  // Update subQuestionId based on new questionId
                    }));
                }
    
                return updatedQuestion;
            });
    
            setSections(updatedSections);
        }
        handleCloseDeleteQuestionDialog();
    };



    const [selectedCondition, setSelectedCondition] = useState({
        sectionIndex: null,
        questionIndex: null,
    });

    const handleEditQuestion = (sectionIndex, questionIndex) => {
        const questionToEdit = sections[sectionIndex].questions[questionIndex];
        setEditedSectionIndex(sectionIndex);
        setEditedQuestionIndex(questionIndex);
        setEditedQuestion({ ...questionToEdit });
        setIsEditQuestionDialogOpen(true);
    };

    // Function to open delete confirmation dialog
    const handleOpenDeleteSectionDialog = (sectionIndex) => {
        setSectionToDelete(sectionIndex);
        setDeleteSectionDialogOpen(true);
    };

    // Function to delete the section after confirmation
    const handleDeleteSection = () => {
        if (sectionToDelete !== null) {
            // Remove the section and reassign sectionIndex
            const updatedSections = sections
                .filter((_, index) => index !== sectionToDelete)
                .map((section, index) => ({
                    ...section,
                    sectionIndex: index,  // Reassign sectionIndex sequentially
                }));
    
            setSections(updatedSections);
            setSectionToDelete(null);
            setDeleteSectionDialogOpen(false);
        }
    };

    // Function to close delete dialog without deleting
    const handleCloseDeleteSectionDialog = () => {
        setSectionToDelete(null);
        setDeleteSectionDialogOpen(false);
    };

    // Move section up
    const handleMoveSectionUp = (index) => {
        if (index > 0) {
            const updatedSections = [...sections];

            // Swap with the section above
            [updatedSections[index], updatedSections[index - 1]] = [updatedSections[index - 1], updatedSections[index]];

            // Update section indices
            updatedSections[index].sectionIndex = index;
            updatedSections[index - 1].sectionIndex = index - 1;

            setSections(updatedSections);
        }
    };

    // Move section down
    const handleMoveSectionDown = (index) => {
        if (index < sections.length - 1) {
            const updatedSections = [...sections];

            // Swap with the section below
            [updatedSections[index], updatedSections[index + 1]] = [updatedSections[index + 1], updatedSections[index]];

            // Update section indices
            updatedSections[index].sectionIndex = index;
            updatedSections[index + 1].sectionIndex = index + 1;

            setSections(updatedSections);
        }
    };



    const handleOpenConditionDialog = (sectionIndex, questionIndex) => {
        setSelectedCondition({ sectionIndex, questionIndex });
        setConditionDialogOpen(true);
    };

    const handleCloseConditionDialog = () => {
        setConditionDialogOpen(false);
        setSelectedCondition({ sectionIndex: null, questionIndex: null });
    };

    // Add a new section
    const handleAddSection = () => {
        setSections((prevSections) => [
            ...prevSections,
            { sectionTitle: `Section ${prevSections.length + 1}`, questions: [] },
        ]);
    };

    // Update section title
    const handleSectionTitleChange = (index, value) => {
        const updatedSections = [...sections];
        updatedSections[index].sectionTitle = value;
        setSections(updatedSections);
    };

    const handleAddTextQuestion = () => {
        if (selectedSectionIndex !== null) {
            setNewQuestion({ questionText: '', type: 'INPUT' }); // Pre-set type as "Text"
            setIsQuestionDialogOpen(true);
        }
    };

    const handleAddSCQQuestion = () => {
        if (selectedSectionIndex !== null) {
            setNewQuestion({ questionText: '', type: 'SCQ', options: [] }); // Pre-set type as "Text"
            setIsQuestionDialogOpen(true);
        }
    };

    const handleAddSlideQuestion = () => {
        if (selectedSectionIndex !== null) {
            setNewQuestion({ questionText: '', type: 'SLIDE', options: [] }); // Pre-set type as "Text"
            setIsQuestionDialogOpen(true);
        }
    };

    const handleAddDropdownQuestion = () => {
        if (selectedSectionIndex !== null) {
            setNewQuestion({ questionText: '', type: 'DROPDOWN', options: [] }); // Pre-set type as "Text"
            setIsQuestionDialogOpen(true);
        }
    };

    const handleAddQuotesQuestion = () => {
        if (selectedSectionIndex !== null) {
            setNewQuestion({ questionText: '', type: 'QUOTE' }); // Pre-set type as "Text"
            setIsQuestionDialogOpen(true);
        }
    };

    const handleAddNotesQuestion = () => {
        if (selectedSectionIndex !== null) {
            setNewQuestion({ questionText: '', type: 'NOTE' }); // Pre-set type as "Text"
            setIsQuestionDialogOpen(true);
        }
    };

    const handleAddTableQuestion = () => {
        if (selectedSectionIndex !== null) {
            setNewQuestion({
                questionText: '',
                type: 'TABLE',
                options: [], // Columns (Table Headers)
                subQuestions: [{ subQuestionText: '', options: [] }], // At least one row with options
            });
            setIsQuestionDialogOpen(true);
        }
    };


    // Save the new question
    const handleSaveQuestion = () => {
        if (selectedSectionIndex !== null) {
            const updatedSections = [...sections];
            const selectedSection = updatedSections[selectedSectionIndex];

            // Generate the question ID (sequence within the section)
            const newQuestionId = selectedSection.questions.length + 1;
            const updatedQuestion = {
                questionId: newQuestionId,
                required: true,
                ...newQuestion,
            };

            // Handle sub-questions for TABLE type
            if (newQuestion.type === "TABLE") {
                updatedQuestion.subQuestions = newQuestion.subQuestions.map((subQuestion, index) => ({
                    subQuestionId: `${newQuestionId}.${index + 1}`, // Decimal-based sub-question ID
                    subQuestionText: subQuestion.subQuestionText || "", // Ensure text is initialized
                    options: subQuestion.options || [], // Ensure options are properly stored
                    required: true,
                }));
            }

            // Add the question to the section
            selectedSection.questions.push(updatedQuestion);

            // Update the state
            setSections(updatedSections);
            setNewQuestion({ questionText: "", type: "", options: [], subQuestions: [] });
            setIsQuestionDialogOpen(false);
        }
    };

    const handleCancelSave = async () => {
        navigate('/admin/dashboard', { state: { view: 'TemplateManagement' } });
    };

    const handleSaveEditedQuestion = () => {
        const updatedSections = [...sections];
        const updatedQuestion = { ...editedQuestion };

        // Ensure TABLE type questions store sub-question options correctly
        if (updatedQuestion.type === "TABLE") {
            updatedQuestion.subQuestions = updatedQuestion.subQuestions.map((subQuestion) => ({
                subQuestionId: subQuestion.subQuestionId,
                subQuestionText: subQuestion.subQuestionText,
                options: subQuestion.options || [], // Ensure options array exists
                required: subQuestion.required,
            }));
        }

        // Update the specific question in the section
        updatedSections[editedSectionIndex].questions[editedQuestionIndex] = updatedQuestion;

        setSections(updatedSections);
        setIsEditQuestionDialogOpen(false);
        setEditedQuestion(null);
        setEditedSectionIndex(null);
        setEditedQuestionIndex(null);
    };


    // Save template
    const handleSaveTemplate = async () => {
        // Ensure subQuestions are correctly structured for TABLE type questions
        const updatedSections = sections.map((section, sectionIndex) => ({
            sectionIndex,  // Include sectionIndex for each section
            ...section,
            questions: section.questions.map(question => {
                if (question.type === "TABLE") {
                    return {
                        ...question,
                        subQuestions: question.subQuestions.map((subQuestion, index) => ({
                            subQuestionId: `${question.questionId}.${index + 1}`,
                            required: subQuestion.required ?? true, // Default required to true
                            subQuestionText: subQuestion.subQuestionText,
                            options: subQuestion.options || [], // Ensure options are included
                        }))
                    };
                }
                return question; // Return as is for other question types
            })
        }));

        const newTemplate = {
            title: templateTitle,
            sections: updatedSections,
        };

        try {
            console.log("Saving Template:", JSON.stringify(newTemplate, null, 2)); // Debugging log
            const response = await axiosInstance.post("/api/v2/templates", newTemplate);
            console.log("Template Saved Successfully:", response.data);
            setNotification({ message: 'Template created successfully!', type: 'success' });
            setTimeout(() => {
                setNotification(null);
                navigate('/admin/dashboard', {
                    state: { view: 'TemplateManagement' },
                });
            }, 2000);
            setTemplateTitle("");
            setSections([]);
        } catch (error) {
            console.error("Error Saving Template:", error);
            setNotification({
                message: 'Failed to save template. Please try again.',
                type: 'error',
            });
            setTimeout(() => setNotification(null), 4000);
        }
    };



    return (
        <Layout>
            {/* Blue Banner */}
            <Box
                sx={{
                    backgroundColor: colors.primaryBannerBlue,
                    color: 'white',
                    padding: { xs: '5px 0', sm: '10px 0' },
                    textAlign: 'center',
                    fontSize: { xs: '1rem', sm: '1.2rem' },
                    fontWeight: '500',
                }}
            >
                Design your Template!
            </Box>

            {/* Content Area */}
            <Box
                sx={{
                    backgroundColor: '#eaecee',
                    minHeight: '100vh',
                    py: 2,
                    px: { xs: 1, sm: 1, md: 2 },
                }}
            >
                <motion.div initial="initial" animate="animate" transition={{ duration: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            width: '100%',
                            minHeight: '100vh',
                            backgroundColor: '#eaecee',
                            boxSizing: 'border-box',
                            gap: { xs: 2, sm: 2 },
                            padding: 0,
                            margin: 0,
                        }}
                    >
                        {/* Left Navigation Pane */}
                        <Box
                            sx={{
                                backgroundColor: '#EDEBE8',
                                width: '28%',
                                flexShrink: 0,
                                boxShadow: '0 6px 14px rgba(0, 0, 0, 0.1)',
                                borderTopLeftRadius: '20px',
                                borderBottomLeftRadius: '20px',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {/* Header Horizontal Bar */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-around', // Evenly distribute buttons
                                    alignItems: 'center',
                                    backgroundColor: '#ffffff',
                                    borderTopLeftRadius: '20px',
                                    mb: 2,
                                }}
                            >
                                {/* Option Buttons */}
                                {['Add', 'Edit'].map((option, index) => (
                                    <Button
                                        key={index}
                                        variant="text"
                                        onClick={() => setSelectedOption(option)}
                                        sx={{
                                            width: "50%",
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            fontSize: '1rem',
                                            backgroundColor: selectedOption === option ? '#EDEBE8' : '#ffffff',
                                            borderTopLeftRadius: option === 'Add' ? '20px' : '0', // Top-left radius for Add button
                                            borderTopRightRadius: '0', // Optional: Top-right radius for Edit button
                                            borderBottomLeftRadius: '0',
                                            borderBottomRightRadius: '0',
                                        }}
                                    >
                                        {option}
                                    </Button>
                                ))}
                            </Box>
                            {/* Content Based on Selected Option */}
                            <Box>
                                {selectedOption === 'Add' ? (
                                    <Box>
                                        <Box
                                            sx={{
                                                paddingTop: '5px',
                                                paddingLeft: '15px',
                                                paddingRight: '15px',
                                                paddingBottom: '10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2, // Spacing between sections
                                                mt: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: '#333',
                                                }}
                                            >
                                                Structure and Design
                                            </Typography>

                                            {/* Button Grid */}
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, 1fr)', // Two buttons per row
                                                    gap: '10px', // Spacing between buttons
                                                }}
                                            >
                                                {/* Add Section Button */}
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleAddSection}
                                                    startIcon={<Add />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.85rem', // Smaller font size for compact buttons
                                                        padding: '6px 12px', // Compact padding
                                                        borderColor: colors.primaryApplicationText,
                                                        color: colors.primaryApplicationText,
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        },
                                                    }}
                                                >
                                                    Section
                                                </Button>

                                                {/* Add Question Button */}
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleAddQuotesQuestion}
                                                    startIcon={<Add />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.85rem',
                                                        padding: '6px 12px',
                                                        borderColor: colors.primaryApplicationText,
                                                        color: colors.primaryApplicationText,
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        },
                                                    }}
                                                >
                                                    Quotes
                                                </Button>
                                                {/* Add Question Button */}
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleAddNotesQuestion}
                                                    startIcon={<Add />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.85rem',
                                                        padding: '6px 12px',
                                                        borderColor: colors.primaryApplicationText,
                                                        color: colors.primaryApplicationText,
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        },
                                                    }}
                                                >
                                                    Note
                                                </Button>
                                            </Box>
                                        </Box>

                                        {/* Add Questions Section */}
                                        <Box
                                            sx={{
                                                height: '1px',
                                                width: '100%',
                                                backgroundColor: colors.primaryApplicationText,
                                                mb: 2, // Add margin below the divider
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                paddingTop: '0',
                                                paddingLeft: '15px',
                                                paddingRight: '15px',
                                                paddingBottom: '10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2, // Spacing between sections
                                                mt: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: '#333',
                                                }}
                                            >
                                                Add Questions
                                            </Typography>

                                            {/* Button Grid */}
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(2, 1fr)', // Two buttons per row
                                                    gap: '10px', // Spacing between buttons
                                                }}
                                            >
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleAddTextQuestion}
                                                    startIcon={<Add />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.85rem',
                                                        padding: '6px 12px',
                                                        borderColor: colors.primaryApplicationText,
                                                        color: colors.primaryApplicationText,
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        },
                                                        opacity: selectedSectionIndex === null ? 0.5 : 1,
                                                        pointerEvents: selectedSectionIndex === null ? 'none' : 'auto',
                                                    }}
                                                >
                                                    Input
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleAddSCQQuestion}
                                                    startIcon={<Add />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.85rem',
                                                        padding: '6px 12px',
                                                        borderColor: colors.primaryApplicationText,
                                                        color: colors.primaryApplicationText,
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        },
                                                        opacity: selectedSectionIndex === null ? 0.5 : 1,
                                                        pointerEvents: selectedSectionIndex === null ? 'none' : 'auto',
                                                    }}
                                                >
                                                    Single Choice
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleAddSlideQuestion}
                                                    startIcon={<Add />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.85rem',
                                                        padding: '6px 12px',
                                                        borderColor: colors.primaryApplicationText,
                                                        color: colors.primaryApplicationText,
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        },
                                                        opacity: selectedSectionIndex === null ? 0.5 : 1,
                                                        pointerEvents: selectedSectionIndex === null ? 'none' : 'auto',
                                                    }}
                                                >
                                                    Slide
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleAddDropdownQuestion}
                                                    startIcon={<Add />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.85rem',
                                                        padding: '6px 12px',
                                                        borderColor: colors.primaryApplicationText,
                                                        color: colors.primaryApplicationText,
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        },
                                                        opacity: selectedSectionIndex === null ? 0.5 : 1,
                                                        pointerEvents: selectedSectionIndex === null ? 'none' : 'auto',
                                                    }}
                                                >
                                                    Dropdown
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={handleAddTableQuestion}
                                                    startIcon={<Add />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 'bold',
                                                        fontSize: '0.85rem',
                                                        padding: '6px 12px',
                                                        borderColor: colors.primaryApplicationText,
                                                        color: colors.primaryApplicationText,
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        },
                                                        opacity: selectedSectionIndex === null ? 0.5 : 1,
                                                        pointerEvents: selectedSectionIndex === null ? 'none' : 'auto',
                                                    }}
                                                >
                                                    Table
                                                </Button>
                                            </Box>
                                        </Box>

                                    </Box>
                                ) : (
                                    <Typography variant="body1">
                                        This is the content for the <b>Edit</b> section. You can edit features here!
                                    </Typography>
                                )}

                            </Box>
                        </Box>

                        {notification && (
                            <Notification
                                message={notification.message}
                                type={notification.type}
                                onClose={() => setNotification(null)}
                            />
                        )}

                        {/* Main Box */}
                        <Box
                            sx={{
                                flexGrow: 1,
                                width: '70%',
                                padding: '20px',
                                backgroundColor: 'white',
                                borderTopRightRadius: '20px',
                                borderBottomRightRadius: '20px',
                                boxShadow: '0 6px 14px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                <Typography variant="h5">Create New Template</Typography>
                                <Box>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSaveTemplate}
                                        sx={{ mr: 2 }}
                                        startIcon={<Save />}
                                        disabled={templateTitle.trim() === "" || sections.length === 0} // Disable if either condition fails
                                    >
                                        Save Template
                                    </Button>
                                    <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelSave}>
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>

                            {/* Template Title */}
                            <TextField
                                label="Template Name"
                                value={templateTitle}
                                onChange={(e) => setTemplateTitle(e.target.value)}
                                fullWidth
                                sx={{ mb: 3 }}
                            />

                            {/* Note for Adding Sections */}
                            {sections.length === 0 && (
                                <Typography
                                    variant="body2"
                                    sx={{ color: "#d32f2f", fontStyle: "italic", mb: 3 }}
                                >
                                    * No sections added yet. Please add a section to enable saving the template.
                                </Typography>
                            )}

                            {/* Sections */}
                            {sections.map((section, sectionIndex) => (
                                <Accordion
                                    key={sectionIndex}
                                    expanded={section.expanded || false} // Each section maintains its own expanded state
                                    onChange={() => {
                                        const updatedSections = [...sections];
                                        updatedSections[sectionIndex].expanded = !updatedSections[sectionIndex].expanded; // Toggle expanded state
                                        setSections(updatedSections);
                                    }}
                                    sx={{
                                        mb: 2,
                                        border: '1px solid #ccc',
                                        '&.Mui-expanded': {
                                            // border: '1px solid #1976d2',
                                        },
                                    }}
                                >
                                    {/* Accordion Summary */}
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        aria-controls={`section-content-${sectionIndex}`}
                                        id={`section-header-${sectionIndex}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            padding: '0 16px',
                                        }}
                                    >
                                        {/* Up Arrow Button */}
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveSectionUp(sectionIndex);
                                            }}
                                            disabled={sectionIndex === 0}
                                            sx={{
                                                color: colors.racineRedDark,
                                                '&:hover': { backgroundColor: '#e3f2fd' },
                                                padding: '6px',
                                            }}
                                        >
                                            <ArrowUpward fontSize="small" />
                                        </IconButton>

                                        {/* Down Arrow Button */}
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveSectionDown(sectionIndex);
                                            }}
                                            disabled={sectionIndex === sections.length - 1}
                                            sx={{
                                                color: colors.primaryApplicationText,
                                                '&:hover': { backgroundColor: '#e3f2fd' },
                                                padding: '6px',
                                            }}
                                        >
                                            <ArrowDownward fontSize="small" />
                                        </IconButton>

                                        {/* Delete Icon Button */}
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenDeleteSectionDialog(sectionIndex);
                                            }}
                                            sx={{
                                                color: colors.racineRedDark,
                                                '&:hover': { backgroundColor: '#ffe6e6' },
                                                padding: '6px',
                                            }}
                                            disabled={selectedSectionIndex === sectionIndex} 
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                        {/* Section Title */}
                                        <Typography
                                            sx={{
                                                flexGrow: 1,
                                                fontWeight: 'bold',
                                                fontSize: '1rem',
                                                lineHeight: 1,           // Remove extra spacing caused by line height
                                                display: 'flex',
                                                alignItems: 'center',    // Center the text vertically within the Typography box
                                                marginLeft: '8px',
                                            }}
                                        >
                                            {`Section ${sectionIndex + 1}: ${section.sectionTitle}`}
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent Accordion toggle when clicking the button
                                                if (selectedSectionIndex === sectionIndex) {
                                                    // If already selected, deselect
                                                    setSelectedSectionIndex(null);
                                                } else {
                                                    // Select the current section
                                                    setSelectedSectionIndex(sectionIndex);
                                                }
                                            }}
                                            sx={{
                                                textTransform: 'none',
                                                fontSize: '0.85rem',
                                                padding: '4px 8px',
                                                variant: selectedSectionIndex === sectionIndex ? 'contained' : 'outlined',
                                                backgroundColor: selectedSectionIndex === sectionIndex ? colors.primaryApplicationText : 'white',
                                                color: selectedSectionIndex === sectionIndex ? 'white' : colors.primaryApplicationText,
                                                borderColor: selectedSectionIndex === sectionIndex ? colors.primaryApplicationText : colors.primaryApplicationText,
                                                '&:hover': {
                                                    backgroundColor: colors.primaryApplicationText,
                                                    color: 'white',
                                                },
                                            }}
                                        >
                                            {selectedSectionIndex === sectionIndex ? 'Editing' : 'Edit'}
                                        </Button>
                                    </AccordionSummary>

                                    {/* Accordion Details */}
                                    <AccordionDetails>
                                        <TextField
                                            label={`Section ${sectionIndex + 1} Title`}
                                            value={section.sectionTitle}
                                            onChange={(e) => handleSectionTitleChange(sectionIndex, e.target.value)}
                                            fullWidth
                                            sx={{ mb: 2 }}
                                        />
                                        {section.questions.map((question, questionIndex) => (
                                            <Box
                                                key={questionIndex}
                                                sx={{
                                                    mb: 2,
                                                    padding: '10px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#fff',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center", // Vertically center align the text and icon
                                                        gap: 1, // Space between text and icon
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: "bold", color: "#555", flex: 1 }}
                                                    >
                                                        Q{questionIndex + 1}: {question.questionText}
                                                    </Typography>
                                                    {/* Edit Question Button */}
                                                    <IconButton
                                                        onClick={() => handleEditQuestion(sectionIndex, questionIndex)}
                                                        sx={{
                                                            color: "#1976d2",
                                                            "&:hover": { backgroundColor: "#e3f2fd" },
                                                        }}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    {/* Add Condition Button with color change based on condition */}
                                                    <IconButton
                                                        onClick={() => handleOpenConditionDialog(sectionIndex, questionIndex)}
                                                        sx={{
                                                            color: question.condition && question.condition.questionId ? colors.racineRedDark : colors.primaryApplicationText,
                                                            "&:hover": {
                                                                backgroundColor: question.condition && question.condition.questionId
                                                                    ? "#fcebea"  // Lighter green background for hover when condition is applied
                                                                    : "#e3f2fd", // Light blue background for hover when no condition is applied
                                                            },
                                                        }}
                                                    >
                                                        <Link fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenDeleteQuestionDialog(sectionIndex, questionIndex);
                                                        }}
                                                        sx={{
                                                            color: colors.racineRedDark,
                                                            '&:hover': { backgroundColor: '#ffe6e6' },
                                                            padding: '6px',
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>

                                                </Box>

                                                {/* Render Condition if it Exists */}
                                                {question.condition && question.condition.questionId && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            mt: 1,
                                                            fontStyle: "italic",
                                                            color: colors.racineRedLight,
                                                            borderLeft: "4px solid #872635",
                                                            paddingLeft: "8px",
                                                        }}
                                                    >
                                                        Condition:{" "}
                                                        {(() => {
                                                            const basedOnQuestion = sections
                                                                .flatMap((section) => section.questions)
                                                                .find(
                                                                    (q) => q.questionId === question.condition.questionId
                                                                );

                                                            if (!basedOnQuestion) return "No condition found.";

                                                            if (
                                                                ["SCQ", "SLIDE", "DROPDOWN"].includes(
                                                                    basedOnQuestion.type
                                                                )
                                                            ) {
                                                                return `${basedOnQuestion.questionText} = ${question.condition.expectedAnswer || "N/A"
                                                                    }`;
                                                            }
                                                            return `${basedOnQuestion.questionText} contains ${question.condition.expectedAnswer || "N/A"
                                                                }`;
                                                        })()}
                                                    </Typography>
                                                )}

                                                {/* Render Based on Question Type */}
                                                {question.type === 'INPUT' && (
                                                    <TextField
                                                        placeholder="Your answer here"
                                                        fullWidth
                                                        disabled
                                                        sx={{ mt: 1 }}
                                                    />
                                                )}
                                                {question.type === 'DROPDOWN' && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                            Select an option:
                                                        </Typography>
                                                        <select
                                                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ddd' }}
                                                            onChange={() => { }} // No state update; interaction allowed but ignored
                                                        >
                                                            <option value="" disabled>
                                                                -- Select an option --
                                                            </option>
                                                            {question.options.map((option, i) => (
                                                                <option key={i} value={option}>
                                                                    {option}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </Box>
                                                )}
                                                {/* Render different question types */}
                                                {question.type === 'QUOTE' && (
                                                    <Box
                                                        sx={{
                                                            borderLeft: '2px solid #872635',
                                                            padding: '10px 15px',
                                                            backgroundColor: '#f0f8ff',
                                                            borderRadius: '4px',
                                                            fontStyle: 'italic',
                                                            fontSize: '1rem',
                                                            color: '#333',
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#872635' }}>
                                                            Quote:
                                                        </Typography>
                                                        <Typography variant="body1">{question.questionText}</Typography>
                                                    </Box>
                                                )}

                                                {question.type === 'NOTE' && (
                                                    <Box
                                                        sx={{
                                                            borderLeft: '4px solid #ffa000',
                                                            padding: '10px 15px',
                                                            backgroundColor: '#fff8e1',
                                                            borderRadius: '4px',
                                                            fontStyle: 'normal',
                                                            fontSize: '1rem',
                                                            color: '#333',
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ffa000' }}>
                                                            Note:
                                                        </Typography>
                                                        <Typography variant="body1">{question.questionText}</Typography>
                                                    </Box>
                                                )}


                                                {question.type === 'SLIDE' && (
                                                    <Box
                                                        sx={{
                                                            position: 'relative',
                                                            width: '100%',
                                                            px: 3,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {/* Slider */}
                                                        <Box sx={{ width: '80%' }}>
                                                            <Slider
                                                                defaultValue={0} // Default position of the slider
                                                                min={0} // Minimum value
                                                                max={(question.options || []).length - 1} // Use a fallback empty array to avoid undefined error
                                                                step={1} // Step size
                                                                marks={(question.options || []).map((option, index) => ({
                                                                    value: index,
                                                                    label: option || `Option ${index + 1}`, // Fallback label if the option is undefined
                                                                }))}
                                                                valueLabelDisplay="off" // Disable the value tooltip
                                                                sx={{
                                                                    '& .MuiSlider-track': {
                                                                        background: colors.racineRedDark, // Gradient for the track
                                                                        height: '6px',
                                                                        border: 'none',
                                                                    },
                                                                    '& .MuiSlider-thumb': {
                                                                        background: colors.racineRedDark, // Gradient for the thumb
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        border: '2px solid white', // White border for contrast
                                                                        '&:hover, &.Mui-focusVisible': {
                                                                            boxShadow: `0 0 0 8px rgba(255, 105, 97, 0.2)`, // Glow effect
                                                                        },
                                                                    },
                                                                    '& .MuiSlider-rail': {
                                                                        backgroundColor: '#e0e0e0', // Grey background rail
                                                                        opacity: 1,
                                                                    },
                                                                    '& .MuiSlider-mark': {
                                                                        height: '8px',
                                                                        width: '8px',
                                                                        backgroundColor: '#e0e0e0', // Grey marks
                                                                        borderRadius: '50%',
                                                                    },
                                                                    '& .MuiSlider-markActive': {
                                                                        background: colors.racineRedDark, // Gradient for active marks
                                                                    },
                                                                }}
                                                                aria-label="SCQ Slider"
                                                            />
                                                        </Box>

                                                    </Box>
                                                )}

                                                {question.type === 'SCQ' && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                            Select an option:
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                            {question.options.map((option, i) => (
                                                                <Box
                                                                    key={i}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name={`scq_${question.questionText}`} // Unique name for each question
                                                                        value={option}
                                                                        onChange={() => { }} // Interaction allowed but ignored
                                                                    />
                                                                    <Typography variant="body2" sx={{ color: '#555' }}>
                                                                        {option}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                )}
                                                {question.type === 'TABLE' && (
                                                    <Box
                                                        sx={{
                                                            mt: 2,
                                                            border: '1px solid #ddd',
                                                            borderRadius: '8px',
                                                            overflow: 'hidden',
                                                            backgroundColor: '#fafafa',
                                                            padding: '12px',
                                                        }}
                                                    >
                                                        {/* Table Header */}
                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: `repeat(${question.options.length + 1}, 1fr)`,
                                                                backgroundColor: '#e0e0e0',
                                                                fontWeight: 'bold',
                                                                textAlign: 'center',
                                                                padding: '10px',
                                                                borderBottom: '2px solid #bbb',
                                                            }}
                                                        >
                                                            <Typography variant="body2" sx={{ padding: '8px', fontWeight: 'bold' }}>
                                                                Sub-Questions
                                                            </Typography>
                                                            {question.options.map((option, i) => (
                                                                <Typography
                                                                    key={i}
                                                                    variant="body2"
                                                                    sx={{
                                                                        padding: '8px',
                                                                        borderLeft: i !== 0 ? '1px solid #bbb' : 'none',
                                                                        fontWeight: 'bold',
                                                                    }}
                                                                >
                                                                    {option}
                                                                </Typography>
                                                            ))}
                                                        </Box>

                                                        {/* Table Rows (Sub-Questions with Single Choice Options) */}
                                                        {question.subQuestions.map((sub, i) => (
                                                            <Box
                                                                key={i}
                                                                sx={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: `repeat(${question.options.length + 1}, 1fr)`,
                                                                    textAlign: 'center',
                                                                    borderBottom: i !== question.subQuestions.length - 1 ? '1px solid #ddd' : 'none',
                                                                    backgroundColor: i % 2 === 0 ? '#f9f9f9' : '#ffffff',
                                                                    padding: '10px 0',
                                                                }}
                                                            >
                                                                {/* Sub-question Text */}
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        padding: '8px',
                                                                        borderRight: '1px solid #ddd',
                                                                        textAlign: 'left',
                                                                        fontWeight: 'bold',
                                                                        color: '#333',
                                                                    }}
                                                                >
                                                                    {sub.subQuestionText}
                                                                </Typography>

                                                                {/* Sub-question Options */}
                                                                {sub.options.map((option, j) => (
                                                                    <Box
                                                                        key={j}
                                                                        sx={{
                                                                            padding: '8px',
                                                                            display: 'flex',
                                                                            justifyContent: 'center',
                                                                            alignItems: 'center',
                                                                            borderLeft: j !== 0 ? '1px solid #ddd' : 'none',
                                                                        }}
                                                                    >
                                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                            <input type="radio" name={`sub-${i}`} value={option} />
                                                                            <Typography variant="body2">{option}</Typography>
                                                                        </label>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}


                                            </Box>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    </Box>
                </motion.div>
            </Box>

            {/* Add Question Dialog */}
            <Dialog
                open={isQuestionDialogOpen}
                onClose={() => setIsQuestionDialogOpen(false)}
                fullWidth
                maxWidth="sm" // Set to small width for better proportions
            >
                {/* Title Section with Background */}
                <Box
                    sx={{
                        backgroundColor: "#f5f5f5", // Light gray background
                        px: 3,
                        py: 2,
                        borderBottom: "1px solid #ccc", // Divider line at the bottom
                    }}
                >
                    <DialogTitle
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 0, // Remove default padding for better layout
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            Add Question
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontStyle: "italic",
                                fontWeight: "bold",
                                color: "#888", // Subtle gray color
                            }}
                        >
                            Type: {newQuestion.type}
                        </Typography>
                    </DialogTitle>
                </Box>
                <DialogContent>
                    {/* Question Text */}
                    <TextField
                        label="Question Text"
                        value={newQuestion.questionText}
                        onChange={(e) => setNewQuestion((prev) => ({ ...prev, questionText: e.target.value }))}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    {/* Question Type */}

                    {/* Options for SCQ */}
                    {(newQuestion.type === 'SCQ' || newQuestion.type === 'SLIDE' || newQuestion.type === 'DROPDOWN') && (
                        <Box>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                Options
                            </Typography>
                            {newQuestion.options.map((option, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <TextField
                                        label={`Option ${index + 1}`}
                                        value={option}
                                        onChange={(e) => {
                                            const updatedOptions = [...newQuestion.options];
                                            updatedOptions[index] = e.target.value;
                                            setNewQuestion((prev) => ({ ...prev, options: updatedOptions }));
                                        }}
                                        fullWidth
                                    />
                                    <Button
                                        onClick={() => {
                                            const updatedOptions = newQuestion.options.filter((_, i) => i !== index);
                                            setNewQuestion((prev) => ({ ...prev, options: updatedOptions }));
                                        }}
                                        variant="outlined"
                                        color="error"
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            ))}
                            <Button
                                onClick={() => {
                                    setNewQuestion((prev) => ({ ...prev, options: [...prev.options, ''] }));
                                }}
                                variant="outlined"
                                startIcon={<Add />}
                                sx={{ mt: 1 }}
                            >
                                Add Option
                            </Button>
                        </Box>
                    )}

                    {/* Sub-Questions for TABLE Type */}
                    {newQuestion.type === 'TABLE' && (
                        <Box>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                Table Options (Columns)
                            </Typography>
                            {newQuestion.options.map((option, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <TextField
                                        label={`Column ${index + 1}`}
                                        value={option}
                                        onChange={(e) => {
                                            const updatedOptions = [...newQuestion.options];
                                            updatedOptions[index] = e.target.value;
                                            setNewQuestion((prev) => ({ ...prev, options: updatedOptions }));
                                        }}
                                        fullWidth
                                    />
                                    <Button
                                        onClick={() => {
                                            const updatedOptions = newQuestion.options.filter((_, i) => i !== index);
                                            setNewQuestion((prev) => ({ ...prev, options: updatedOptions }));
                                        }}
                                        variant="outlined"
                                        color="error"
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            ))}
                            <Button
                                onClick={() => setNewQuestion((prev) => ({ ...prev, options: [...prev.options, ''] }))}
                                variant="outlined"
                                startIcon={<Add />}
                                sx={{ mt: 1 }}
                            >
                                Add Column
                            </Button>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="body1" sx={{ mb: 1 }}>
                                Table Sub-Questions (Rows)
                            </Typography>
                            {newQuestion.subQuestions.map((subQuestion, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        mb: 2,
                                        p: 2,
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                    }}
                                >
                                    <TextField
                                        label={`Row ${index + 1} - Sub-Question`}
                                        value={subQuestion.subQuestionText}
                                        onChange={(e) => {
                                            const updatedSubQuestions = [...newQuestion.subQuestions];
                                            updatedSubQuestions[index].subQuestionText = e.target.value;
                                            setNewQuestion((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
                                        }}
                                        fullWidth
                                    />

                                    {/* Sub-Question Options */}
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Options for this sub-question:
                                    </Typography>
                                    {subQuestion.options.map((option, optionIndex) => (
                                        <Box
                                            key={optionIndex}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1,
                                            }}
                                        >
                                            <TextField
                                                label={`Option ${optionIndex + 1}`}
                                                value={option}
                                                onChange={(e) => {
                                                    const updatedSubQuestions = [...newQuestion.subQuestions];
                                                    updatedSubQuestions[index].options[optionIndex] = e.target.value;
                                                    setNewQuestion((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
                                                }}
                                                fullWidth
                                            />
                                            <Button
                                                onClick={() => {
                                                    const updatedSubQuestions = [...newQuestion.subQuestions];
                                                    updatedSubQuestions[index].options = updatedSubQuestions[index].options.filter((_, i) => i !== optionIndex);
                                                    setNewQuestion((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
                                                }}
                                                variant="outlined"
                                                color="error"
                                            >
                                                Remove
                                            </Button>
                                        </Box>
                                    ))}
                                    <Button
                                        onClick={() => {
                                            const updatedSubQuestions = [...newQuestion.subQuestions];
                                            updatedSubQuestions[index].options.push('');
                                            setNewQuestion((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
                                        }}
                                        variant="outlined"
                                        startIcon={<Add />}
                                    >
                                        Add Option
                                    </Button>

                                    <Button
                                        onClick={() => {
                                            const updatedSubQuestions = newQuestion.subQuestions.filter((_, i) => i !== index);
                                            setNewQuestion((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
                                        }}
                                        variant="outlined"
                                        color="error"
                                        sx={{ mt: 1 }}
                                    >
                                        Remove Row
                                    </Button>
                                </Box>
                            ))}
                            <Button
                                onClick={() =>
                                    setNewQuestion((prev) => ({
                                        ...prev,
                                        subQuestions: [...prev.subQuestions, { subQuestionText: '', options: [] }],
                                    }))
                                }
                                variant="outlined"
                                startIcon={<Add />}
                                sx={{ mt: 1 }}
                            >
                                Add Row
                            </Button>
                        </Box>
                    )}

                </DialogContent>
                {/* Dialog Actions */}
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setIsQuestionDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveQuestion}
                        disabled={
                            !newQuestion.questionText.trim() || // Disable if question text is empty
                            ((newQuestion.type === "SCQ" ||
                                newQuestion.type === "SLIDE" ||
                                newQuestion.type === "DROPDOWN") &&
                                newQuestion.options.filter((option) => option.trim() !== "").length <
                                2) || // Disable if less than 2 valid options
                            (newQuestion.type === "TABLE" &&
                                (newQuestion.options.filter((option) => option.trim() !== "").length <
                                    2 || // Require at least 2 columns
                                    newQuestion.subQuestions.filter((sub) => sub.subQuestionText.trim() !== "").length <
                                    1)) // Require at least 1 sub-question
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={conditionDialogOpen} onClose={handleCloseConditionDialog}>
                <DialogTitle>Add Condition</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Define the condition for this question.
                    </Typography>

                    {/* Based on Question Dropdown */}
                    <TextField
                        select
                        label="Based on Question"
                        value={
                            (selectedCondition.sectionIndex !== null &&
                                selectedCondition.questionIndex !== null &&
                                sections[selectedCondition.sectionIndex].questions[selectedCondition.questionIndex]?.condition?.questionId) || ""
                        }


                        onChange={(e) => {
                            const updatedSections = [...sections];
                            const question = updatedSections[selectedCondition.sectionIndex].questions[selectedCondition.questionIndex];
                            question.condition = {
                                ...question.condition,
                                questionId: e.target.value,
                            };
                            setSections(updatedSections);
                        }}
                        fullWidth
                        SelectProps={{ native: false }}
                        sx={{ mb: 2 }}
                    >
                        <MenuItem value="" disabled>
                            Select a question
                        </MenuItem>
                        {sections.flatMap((section) =>
                            section.questions.map((q) => (
                                <MenuItem key={q.questionId} value={q.questionId}>
                                    {q.questionText}
                                </MenuItem>
                            ))
                        )}
                    </TextField>

                    {/* Expected Answer Field */}
                    {(() => {
                        const basedOnQuestion =
                            selectedCondition.sectionIndex !== null &&
                            selectedCondition.questionIndex !== null &&
                            sections
                                .flatMap((section) => section.questions)
                                .find(
                                    (q) =>
                                        q.questionId ===
                                        sections[selectedCondition.sectionIndex].questions[selectedCondition.questionIndex]?.condition?.questionId
                                );

                        if (basedOnQuestion?.type === "SCQ" || basedOnQuestion?.type === "SLIDE" || basedOnQuestion?.type === "DROPDOWN") {
                            // Render dropdown for SCQ, SLIDE, or DROPDOWN
                            return (
                                <TextField
                                    select
                                    label="Expected Answer"
                                    value={
                                        (selectedCondition.sectionIndex !== null &&
                                            selectedCondition.questionIndex !== null &&
                                            sections[selectedCondition.sectionIndex].questions[selectedCondition.questionIndex]?.condition?.expectedAnswer) || ""
                                    }
                                    onChange={(e) => {
                                        const updatedSections = [...sections];
                                        const question = updatedSections[selectedCondition.sectionIndex].questions[selectedCondition.questionIndex];
                                        question.condition = {
                                            ...question.condition,
                                            expectedAnswer: e.target.value,
                                        };
                                        setSections(updatedSections);
                                    }}
                                    fullWidth
                                    SelectProps={{ native: false }}
                                >
                                    {basedOnQuestion?.options?.map((option, index) => (
                                        <MenuItem key={index} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            );
                        } else {
                            // Render input box for other types
                            return (
                                <TextField
                                    label="Expected Answer"
                                    value={
                                        (selectedCondition.sectionIndex !== null &&
                                            selectedCondition.questionIndex !== null &&
                                            sections[selectedCondition.sectionIndex].questions[selectedCondition.questionIndex]?.condition?.expectedAnswer) || ""
                                    }

                                    onChange={(e) => {
                                        const updatedSections = [...sections];
                                        const question = updatedSections[selectedCondition.sectionIndex].questions[selectedCondition.questionIndex];
                                        question.condition = {
                                            ...question.condition,
                                            expectedAnswer: e.target.value,
                                        };
                                        setSections(updatedSections);
                                    }}
                                    fullWidth
                                />
                            );
                        }
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConditionDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCloseConditionDialog}
                    >
                        Save Condition
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={deleteSectionDialogOpen}
                onClose={handleCloseDeleteSectionDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this section? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteSectionDialog} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteSection}
                        variant="contained"
                        color="error"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={isEditQuestionDialogOpen}
                onClose={() => setIsEditQuestionDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Edit Question</DialogTitle>
                <DialogContent>
                    {/* Question Text */}
                    <TextField
                        label="Question Text"
                        value={editedQuestion?.questionText || ""}
                        onChange={(e) =>
                            setEditedQuestion((prev) => ({ ...prev, questionText: e.target.value }))
                        }
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    {/* Options for SCQ/DROPDOWN/SLIDE */}
                    {(editedQuestion?.type === "SCQ" ||
                        editedQuestion?.type === "DROPDOWN" ||
                        editedQuestion?.type === "SLIDE") && (
                            <Box>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    Options
                                </Typography>
                                {editedQuestion?.options?.map((option, index) => (
                                    <Box
                                        key={index}
                                        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                                    >
                                        <TextField
                                            label={`Option ${index + 1}`}
                                            value={option}
                                            onChange={(e) => {
                                                const updatedOptions = [...editedQuestion.options];
                                                updatedOptions[index] = e.target.value;
                                                setEditedQuestion((prev) => ({ ...prev, options: updatedOptions }));
                                            }}
                                            fullWidth
                                        />
                                        <Button
                                            onClick={() => {
                                                const updatedOptions = editedQuestion.options.filter(
                                                    (_, i) => i !== index
                                                );
                                                setEditedQuestion((prev) => ({ ...prev, options: updatedOptions }));
                                            }}
                                            variant="outlined"
                                            color="error"
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                ))}
                                <Button
                                    onClick={() =>
                                        setEditedQuestion((prev) => ({
                                            ...prev,
                                            options: [...(prev.options || []), ""],
                                        }))
                                    }
                                    variant="outlined"
                                    startIcon={<Add />}
                                    sx={{ mt: 1 }}
                                >
                                    Add Option
                                </Button>
                            </Box>
                        )}

                    {/* Edit Table Question */}
                    {editedQuestion?.type === "TABLE" && (
                        <Box>
                            {/* Table Headers (Columns) */}
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                Table Options (Columns)
                            </Typography>
                            {editedQuestion?.options?.map((option, index) => (
                                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <TextField
                                        label={`Column ${index + 1}`}
                                        value={option}
                                        onChange={(e) => {
                                            const updatedOptions = [...editedQuestion.options];
                                            updatedOptions[index] = e.target.value;
                                            setEditedQuestion((prev) => ({ ...prev, options: updatedOptions }));
                                        }}
                                        fullWidth
                                    />
                                    <Button
                                        onClick={() => {
                                            const updatedOptions = editedQuestion.options.filter((_, i) => i !== index);
                                            setEditedQuestion((prev) => ({ ...prev, options: updatedOptions }));
                                        }}
                                        variant="outlined"
                                        color="error"
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            ))}
                            <Button
                                onClick={() =>
                                    setEditedQuestion((prev) => ({ ...prev, options: [...prev.options, ""] }))
                                }
                                variant="outlined"
                                startIcon={<Add />}
                                sx={{ mt: 1 }}
                            >
                                Add Column
                            </Button>

                            <Divider sx={{ my: 2 }} />

                            {/* Table Sub-Questions (Rows) */}
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                Table Sub-Questions (Rows)
                            </Typography>
                            {editedQuestion?.subQuestions?.map((subQuestion, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        border: "1px solid #ddd",
                                        borderRadius: "8px",
                                        backgroundColor: "#f9f9f9",
                                    }}
                                >
                                    {/* Sub-Question Text */}
                                    <TextField
                                        label={`Sub-Question ${index + 1}`}
                                        value={subQuestion.subQuestionText}
                                        onChange={(e) => {
                                            const updatedSubQuestions = [...editedQuestion.subQuestions];
                                            updatedSubQuestions[index].subQuestionText = e.target.value;
                                            setEditedQuestion((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
                                        }}
                                        fullWidth
                                        sx={{ mb: 1 }}
                                    />

                                    {/* Options for Each Sub-Question */}
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
                                        Options for this Row:
                                    </Typography>
                                    {subQuestion?.options?.map((option, optIndex) => (
                                        <Box key={optIndex} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                            <TextField
                                                label={`Option ${optIndex + 1}`}
                                                value={option}
                                                onChange={(e) => {
                                                    const updatedSubQuestions = [...editedQuestion.subQuestions];
                                                    updatedSubQuestions[index].options[optIndex] = e.target.value;
                                                    setEditedQuestion((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
                                                }}
                                                fullWidth
                                            />
                                            <Button
                                                onClick={() => {
                                                    const updatedSubQuestions = [...editedQuestion.subQuestions];
                                                    updatedSubQuestions[index].options = updatedSubQuestions[index].options.filter(
                                                        (_, i) => i !== optIndex
                                                    );
                                                    setEditedQuestion((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
                                                }}
                                                variant="outlined"
                                                color="error"
                                            >
                                                Remove
                                            </Button>
                                        </Box>
                                    ))}
                                    <Button
                                        onClick={() => {
                                            const updatedSubQuestions = [...editedQuestion.subQuestions];
                                            updatedSubQuestions[index].options = [...(updatedSubQuestions[index].options || []), ""];
                                            setEditedQuestion((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
                                        }}
                                        variant="outlined"
                                        startIcon={<Add />}
                                        sx={{ mt: 1 }}
                                    >
                                        Add Option to Row
                                    </Button>

                                    {/* Remove Sub-Question Button */}
                                    <Button
                                        onClick={() => {
                                            const updatedSubQuestions = editedQuestion.subQuestions.filter((_, i) => i !== index);
                                            setEditedQuestion((prev) => ({ ...prev, subQuestions: updatedSubQuestions }));
                                        }}
                                        variant="outlined"
                                        color="error"
                                        sx={{ mt: 1, ml: 1 }}
                                    >
                                        Remove Row
                                    </Button>
                                </Box>
                            ))}
                            <Button
                                onClick={() =>
                                    setEditedQuestion((prev) => ({
                                        ...prev,
                                        subQuestions: [...prev.subQuestions, { subQuestionText: "", options: [] }],
                                    }))
                                }
                                variant="outlined"
                                startIcon={<Add />}
                                sx={{ mt: 1 }}
                            >
                                Add Row
                            </Button>
                        </Box>
                    )}

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsEditQuestionDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEditedQuestion}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={isDeleteQuestionDialogOpen}
                onClose={handleCloseDeleteQuestionDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Delete Question</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to delete this question? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteQuestionDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteQuestion}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

        </Layout>
    );
};

export default TemplateCreate;
